import * as tenantStackActions from '@Actions/tenantStack';
import { PayloadAction } from '@reduxjs/toolkit';
import { stackCheckGreengrass } from '@Services/tenantStack';
import {
	TenantStackCheckRequest,
	TenantStackCheckResponse,
} from '@Types/tenatStack';
import { AxiosResponse } from 'axios';
import { Task } from 'redux-saga';
import {
	call,
	cancel,
	cancelled,
	delay,
	fork,
	put,
	take,
} from 'redux-saga/effects';

const INITIAL_DELAY_SEC = 3; // ? possible lambda's cold start time
const BETWEEN_POLLING_TRIES_DELAY_SEC = 5; // ? delay between polling tries

type PollResponse = AxiosResponse<TenantStackCheckResponse>;
function* pollTenantStackCheckSaga(
	action: PayloadAction<TenantStackCheckRequest>,
) {
	const {
		tenantStackCheckPollResolved,
		tenantStackCheckPollRejected,
		tenantStackCheckPollStop,
	} = tenantStackActions;
	try {
		console.log(action.payload);
		while (true) {
			const res: PollResponse = yield call(stackCheckGreengrass);
			if (res.data) {
				yield put(tenantStackCheckPollResolved(res.data));
				yield put(tenantStackCheckPollStop());
			} else {
				yield delay(1000 * BETWEEN_POLLING_TRIES_DELAY_SEC); // * Poll continues...
			}
		}
	} catch (e) {
		console.error(e);
		yield put(tenantStackCheckPollRejected());
		yield put(tenantStackCheckPollStop());
	} finally {
		// * This block gets called if the task (saga) gets cancelled
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		if (yield cancelled()) console.log('poll was cancelled');
	}
}

/**
 * Custom implementation of a redux-saga effect
 * This saga is in charge of auto-cancel polling action of: check tenant stack request.
 */
function* stopTenantStateCheckSaga(
	action: PayloadAction<TenantStackCheckRequest>,
) {
	const { tenantStackCheckPollTimeout } = tenantStackActions;
	const tOut = action.payload.timeoutSec;
	console.log(`poll connection Async will stop after ${tOut} seconds!`);
	try {
		yield delay(tOut * 1000);
		yield put(tenantStackCheckPollTimeout());
	} catch (error) {
		console.log(error);
	} finally {
		// * This block gets called if the task (saga) gets cancelled
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		if (yield cancelled()) console.log('stop condition was cancelled');
	}
}

/**
 * Custom implementation of a redux-saga effect
 * This saga is in charge of a CANCELLABLE polling action of: check tenant stack request.
 * CANCELLABLE = (either on purpose or after a timeout)
 */
function* watchPollSystemConnectionTestState() {
	const {
		tenantStackCheckPoll,
		tenantStackCheckPollResolved,
		tenantStackCheckPollRejected,
		tenantStackCheckPollStop,
	} = tenantStackActions;
	// * Custom implementation, similar to takeLatest but it's cancellable. Check pollTenantStackCheckSaga
	while (true) {
		const action: PayloadAction<TenantStackCheckRequest> = yield take([
			tenantStackCheckPoll,
		]);
		console.log('tenantStackCheckPoll action taken!');
		yield delay(1000 * INITIAL_DELAY_SEC);
		const task: Task = yield fork(
			pollTenantStackCheckSaga,
			action as never,
		);
		const stopConditionTask: Task = yield fork(
			stopTenantStateCheckSaga,
			action,
		);
		yield take([
			tenantStackCheckPollStop,
			tenantStackCheckPollResolved,
			tenantStackCheckPollRejected,
		]);
		if (task.isRunning()) yield cancel(task);
		if (stopConditionTask.isRunning()) yield cancel(stopConditionTask);
	}
}

export default [
	watchPollSystemConnectionTestState(), //
];
